/**
 * EfolderPreviewPane - Full-height document preview pane for eFolder
 *
 * Design principle: The primary user task is reading documents.
 * Everything else should get out of the way.
 *
 * The sidebar already shows document metadata (name, status, download button),
 * so the preview pane is purely for displaying the document content.
 */

import React from 'react';
import {
  ExternalLink,
  Loader2,
  FileText,
  File,
  Clock,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PDFViewer from '@/components/contacts/PDFViewer';
import type { EncompassAttachment } from '@/types/encompass-documents';

interface EfolderPreviewPaneProps {
  selectedAttachment: EncompassAttachment | null;
  previewUrl: string | null;
  previewLoading: boolean;
  onRetrySync: () => void;
  syncing: boolean;
}

export function EfolderPreviewPane({
  selectedAttachment,
  previewUrl,
  previewLoading,
  onRetrySync,
  syncing
}: EfolderPreviewPaneProps) {
  // Empty state - no document selected
  if (!selectedAttachment) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-muted/10">
        <div className="rounded-full bg-muted/50 p-6 mb-4">
          <ExternalLink className="h-12 w-12 text-muted-foreground/50" />
        </div>
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          Select a document to preview
        </h3>
        <p className="text-sm text-muted-foreground/70 max-w-sm">
          Choose a document from the sidebar to view its contents here
        </p>
      </div>
    );
  }

  // Loading state
  if (previewLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-muted/10">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Loading preview...</p>
      </div>
    );
  }

  // PDF ready - FULL HEIGHT, no header.
  // initialPage jumps to a content-search hit when the row carries matchInfo;
  // best-effort (PDF page numbering may differ from the FTS-derived guess).
  if (previewUrl) {
    const initialPage = selectedAttachment.matchInfo?.pageNumber ?? undefined;
    return (
      <PDFViewer
        url={previewUrl}
        showBottomControls={false}
        className="h-full"
        initialPage={initialPage ?? undefined}
      />
    );
  }

  // Pending state - document being downloaded
  if (selectedAttachment.download_status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-muted/10">
        <div className="rounded-full bg-yellow-100 p-4 mb-4">
          <Clock className="h-10 w-10 text-yellow-600" />
        </div>
        <h3 className="text-lg font-medium mb-2">Document is being downloaded</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          This document will be available for preview after the next sync completes
        </p>
        <Button
          variant="outline"
          onClick={onRetrySync}
          disabled={syncing}
        >
          {syncing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {syncing ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>
    );
  }

  // Failed state - download error
  if (selectedAttachment.download_status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-muted/10">
        <div className="rounded-full bg-red-100 p-4 mb-4">
          <AlertTriangle className="h-10 w-10 text-red-600" />
        </div>
        <h3 className="text-lg font-medium mb-2">Download failed</h3>
        {selectedAttachment.download_error && (
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            {selectedAttachment.download_error}
          </p>
        )}
        <Button
          variant="outline"
          onClick={onRetrySync}
          disabled={syncing}
        >
          {syncing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {syncing ? 'Retrying...' : 'Retry Sync'}
        </Button>
      </div>
    );
  }

  // Deleted state - file removed from Encompass
  if (selectedAttachment.download_status === 'deleted') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-muted/10">
        <div className="rounded-full bg-gray-100 p-4 mb-4">
          <Trash2 className="h-10 w-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium mb-2">Document no longer available</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          This attachment was deleted from Encompass
        </p>
      </div>
    );
  }

  // Skipped state - TD or unsupported file type
  if (selectedAttachment.download_status === 'skipped') {
    const isTdSkipped = selectedAttachment.download_error?.includes('Transferred Data');
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-muted/10">
        <div className="rounded-full bg-gray-100 p-4 mb-4">
          <File className="h-10 w-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium mb-2">
          {isTdSkipped ? 'Transferred Data Loan' : 'File Type Not Supported'}
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          {isTdSkipped
            ? 'Downloads are disabled for historical Transferred Data loans'
            : 'This document type cannot be previewed'}
        </p>
      </div>
    );
  }

  // Fallback - preview not available
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-muted/10">
      <div className="rounded-full bg-muted p-4 mb-4">
        <FileText className="h-10 w-10 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-medium mb-2">Preview not available</h3>
      <p className="text-sm text-muted-foreground">
        This document type cannot be previewed
      </p>
    </div>
  );
}
